import { useEffect, useRef, useState } from 'react';
import { ApiError, loginWithTelegram, type TelegramUser } from '../lib/api.js';

interface TelegramLoginProps {
  botUsername: string;
  onLogin: (user: TelegramUser) => void | Promise<void>;
}

let widgetSequence = 0;

/** Telegram signs the widget payload; only the server may authenticate it. */
export function TelegramLogin({ botUsername, onLogin }: TelegramLoginProps): JSX.Element {
  const holderRef = useRef<HTMLDivElement>(null);
  const onLoginRef = useRef(onLogin);
  onLoginRef.current = onLogin;
  const [attempt, setAttempt] = useState(0);
  const [status, setStatus] = useState<'loading' | 'ready' | 'authenticating' | 'failed'>('loading');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const holder = holderRef.current;
    if (!holder) return;
    let active = true;
    let authenticating = false;
    let loginController: AbortController | null = null;
    let loginTimer: ReturnType<typeof setTimeout> | undefined;
    const callbackName = `onKinopTelegramAuth${++widgetSequence}`;
    const globals = window as unknown as Record<string, unknown>;
    setStatus('loading');
    setError(null);
    const failLoad = () => {
      if (active && !authenticating) setStatus('failed');
    };
    const loadTimer = setTimeout(failLoad, 15000);
    const ready = () => {
      clearTimeout(loadTimer);
      if (active && !authenticating) setStatus('ready');
    };
    // The script inserts an iframe asynchronously. Its own load event only
    // confirms the script download, not that the login button is available.
    const observer = new MutationObserver(() => {
      const frame = holder.querySelector('iframe');
      if (frame) {
        frame.addEventListener('load', ready, { once: true });
        observer.disconnect();
      }
    });
    observer.observe(holder, { childList: true, subtree: true });
    globals[callbackName] = async (payload: Record<string, unknown>): Promise<void> => {
      if (!active || authenticating) return;
      authenticating = true;
      clearTimeout(loadTimer);
      setStatus('authenticating');
      setError(null);
      loginController = new AbortController();
      loginTimer = setTimeout(() => loginController?.abort(), 20000);
      try {
        const user = await loginWithTelegram(payload, loginController.signal);
        if (!active) return;
        await onLoginRef.current(user);
        if (active) setStatus('ready');
      } catch (loginError) {
        if (!active) return;
        setStatus('ready');
        setError(loginController.signal.aborted
          ? 'Сервер не ответил вовремя. Попробуйте войти ещё раз.'
          : loginError instanceof ApiError ? loginError.message : 'Войти не получилось. Попробуйте ещё раз.');
      } finally {
        clearTimeout(loginTimer);
        authenticating = false;
      }
    };

    const script = document.createElement('script');
    script.src = 'https://telegram.org/js/telegram-widget.js?22';
    script.async = true;
    script.dataset.telegramLogin = botUsername;
    script.dataset.size = 'large';
    script.dataset.radius = '12';
    script.dataset.onauth = `${callbackName}(user)`;
    script.dataset.requestAccess = 'write';
    script.onerror = failLoad;
    // Defer one task so StrictMode cleanup cannot leave a downloaded script
    // creating an orphan widget with an already removed callback.
    const appendTimer = setTimeout(() => { if (active) holder.appendChild(script); }, 0);
    return () => {
      active = false;
      clearTimeout(appendTimer);
      clearTimeout(loadTimer);
      clearTimeout(loginTimer);
      loginController?.abort();
      observer.disconnect();
      holder.replaceChildren();
      delete globals[callbackName];
    };
  }, [botUsername, attempt]);

  return (
    <div className="tg-login" aria-busy={status === 'authenticating'}>
      <div className="tg-login__holder" ref={holderRef} />
      <p className="tg-login__note" role="status">
        {status === 'loading' && 'Загружаем вход через Telegram…'}
        {status === 'authenticating' && 'Проверяем вход, подождите…'}
      </p>
      {status === 'failed' && (
        <div className="tg-login__note" role="alert">
          <p>Не удалось загрузить кнопку Telegram. Проверьте подключение или откройте ссылку в обычном браузере.</p>
          <button type="button" className="chip" onClick={() => setAttempt((value) => value + 1)}>Повторить загрузку Telegram</button>
        </div>
      )}
      {status === 'ready' && <p className="tg-login__note">Если окно входа не открывается, разрешите всплывающие окна или откройте эту ссылку в Chrome / Safari.</p>}
      {error && <p className="error" role="alert">{error}</p>}
    </div>
  );
}
